/**
 * Chunking for the manual CRM send, so one click can safely address a whole roster.
 *
 * 🚨 The recipient list is UNCAPPED. Since the Buyer Matches page started rendering the
 * buyers we hold no breakdown for, "Select · All matches" can hand the send every matched
 * buyer — 195 on one real Dallas deal, and rosters run to 1,118.
 *
 * `createGHLOpportunity` runs ~2 buyers/s (BUYER_CONCURRENCY 8 against GHL's ~8 req/s
 * ceiling) under a 240s timeout, so ONE call tops out near 460 buyers. Past that the hop
 * is cut off while the endpoint keeps working, and "timeout" means opportunities MAY exist
 * that nobody can account for. `functions/autoSendToCrm.js` has chunked at 300 for exactly
 * this reason since auto-send shipped; this is the same guard for the hand-picked path,
 * which had none. Keep SEND_CHUNK_SIZE in step with that constant.
 */
export const SEND_CHUNK_SIZE = 300;

export function chunkBuyerIds(buyerIds, size = SEND_CHUNK_SIZE) {
    const ids = Array.isArray(buyerIds) ? buyerIds : Array.from(buyerIds || []);
    if (ids.length === 0) return [];
    const chunks = [];
    for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
    return chunks;
}

// Counts the endpoints report. `created` is the opportunity path, `tagged` the tag path —
// summed only when a response actually carried the key, so a merged tag result never grows
// a phantom `created: 0` that the summary formatters would have to special-case.
const SUM_KEYS = ['created', 'tagged', 'skipped', 'failed'];

/**
 * Fold one chunk's response `data` into the running total, so a chunked send still reads
 * as ONE result: summed counts, concatenated per-buyer rows, and any warning flag latched
 * on rather than overwritten by a later clean chunk.
 */
export function mergeChunkData(acc, data) {
    if (!data) return acc;
    if (!acc) return { ...data, results: [...(data.results || [])] };

    const merged = { ...acc, ...data };
    for (const key of SUM_KEYS) {
        if (key in acc || key in data) merged[key] = (acc[key] || 0) + (data[key] || 0);
    }
    merged.results = [...(acc.results || []), ...(data.results || [])];
    // Latched, never overwritten: a truncated message in chunk 1 is still truncated
    // after chunk 2 comes back clean.
    if (acc.messageTruncated || data.messageTruncated) merged.messageTruncated = true;
    if (acc.dealLinkWarning || data.dealLinkWarning) merged.dealLinkWarning = acc.dealLinkWarning || data.dealLinkWarning;
    if (acc.needsSelection || data.needsSelection) merged.needsSelection = true;
    return merged;
}

/**
 * Should the loop send the next chunk?
 *
 * Deliberately NARROWER than autoSendToCrm's rule, which stops whenever a chunk created
 * nothing. Here a well-formed 200 is a clean answer even at zero — a chunk where every
 * buyer lacked contact details is "300 skipped", not a CRM in an unknown state, and
 * abandoning the rest of the send over it would strand buyers for no reason.
 *
 * We stop on:
 *   - a non-200 / timeout: the endpoint may still be working, so piling more writes onto
 *     a CRM we can't account for is exactly the wrong move;
 *   - needsSelection: the configured pipeline/stage no longer exists in the CRM, so every
 *     remaining chunk would fail the same way.
 */
export function shouldContinueAfterChunk(res) {
    if (!res || res.status !== 200 || !res.data) return false;
    return !res.data.needsSelection;
}

/**
 * Did the whole selection actually go out?
 *
 * 🚨 The HTTP status of the LAST call is not the answer. A run that sends chunk 1 fine
 * and then stops on chunk 2 — because the configured pipeline vanished, say — ends on a
 * 200, and reporting that as plain success would say "300 created" on a 1,118-buyer send
 * with 800 buyers never attempted. The only honest test is: did every chunk go, and did
 * the last one come back clean.
 */
export function isCleanFullRun({ chunksSent, chunksTotal, lastRes }) {
    return chunksSent === chunksTotal && shouldContinueAfterChunk(lastRes);
}
